import { useEffect } from 'react'
import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import i18n from '@/application/i18n'
import { resolveLanguage } from '@/application/i18n/language'
import { queryClient } from '@/application/queryClient'
import {
  investigationKeys,
  resolveInvestigationRef,
  useGetInvestigationById,
} from '@/application/services/investigation.service'
import { useGetSiteIdentity } from '@/application/services/settings.service'
import { useDocumentMeta, summarize } from '@/application/hooks/useDocumentMeta'
import { absoluteImageUrl } from '@/application/seo/site'
import { useLocalizedUrl } from '@/application/hooks/useLocalizedUrl'
import type { InvestigationRefResolution } from '@/domains/types'
import { publicLayoutRoute } from '@/ui/pages/public'
import { InvestigationReadView } from './components/read/InvestigationReadView'
import { InvestigationNotFound } from './components/read/InvestigationNotFound'
import { ReadModeSkeleton } from './components/read/ReadModeSkeleton'

const RESOLVE_STALE_TIME_MS = 60_000

export const publicInvestigationDetailRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'investigations/$slug',
  // The URL segment is a slug of any language, a historical slug or a UUID:
  // resolve it first and redirect (replace) to the current slug of the
  // language of the URL. The language is already in i18next before the router
  // runs (see `i18n/index.ts`).
  loader: async ({ params, location }) => {
    const locale = resolveLanguage(i18n.language)
    // A failing or unexpected resolve (network, older backend without the
    // endpoint) must never break the page: show it through the segment as an
    // id, which the read endpoint accepts for a UUID. No redirect, so no loop.
    const resolution = await queryClient
      .fetchQuery({
        queryKey: investigationKeys.resolve(params.slug, locale),
        queryFn: () => resolveInvestigationRef(params.slug, locale),
        staleTime: RESOLVE_STALE_TIME_MS,
        retry: false,
      })
      .catch(
        (): InvestigationRefResolution => ({
          id: params.slug,
          locale,
          slug: params.slug,
          slugs: {},
          canonical: true,
        }),
      )
    if (resolution && !resolution.canonical) {
      throw redirect({
        to: '/investigations/$slug',
        params: { slug: resolution.slug },
        search: location.search,
        hash: location.hash,
        replace: true,
      })
    }
    return { resolution }
  },
  pendingComponent: ReadModeSkeleton,
  component: PublicInvestigationDetailPage,
})

/**
 * Public investigation detail page. The public site only ever shows the
 * article (read-mode) view — all editing/management lives under `manage/`.
 * This shell reads the resolved investigation, sets the page's SEO/social meta
 * (title/description/OG image — `InvestigationReadView` owns its own JSON-LD
 * and hreflang tags but not these), keeps the URL on the slug of the current
 * language, and renders the article view. Data stays keyed by UUID.
 */
export function PublicInvestigationDetailPage() {
  const { i18n: activeI18n } = useTranslation()
  const { slug } = publicInvestigationDetailRoute.useParams()
  const { resolution } = publicInvestigationDetailRoute.useLoaderData()
  const navigate = useNavigate()
  const locale = resolveLanguage(activeI18n.language)
  const investigationId = resolution?.id ?? ''
  const localeSlug = resolution?.slugs?.[locale]

  const { data: investigation } = useGetInvestigationById(investigationId)
  const { data: siteIdentity } = useGetSiteIdentity(locale)

  const pageUrl = useLocalizedUrl(`/investigations/${localeSlug ?? slug}`)

  // The language switcher rewrites the URL in place (same route, same params),
  // so the loader does not run again: swap in the slug of the new language,
  // which is already known — no request.
  useEffect(() => {
    if (!localeSlug || localeSlug === slug) return
    void navigate({
      to: '/investigations/$slug',
      params: { slug: localeSlug },
      search: true,
      hash: true,
      replace: true,
    })
  }, [localeSlug, slug, navigate])

  // Keeps the tab title and the JS-rendered crawlers (Googlebot) in sync.
  // Script-less social crawlers are served the backend `share` page instead.
  useDocumentMeta(
    investigation
      ? {
          title: investigation.name,
          description: summarize(investigation.description),
          imageUrl: absoluteImageUrl(investigation.imageUrl),
          url: pageUrl,
          siteName: siteIdentity?.siteName,
        }
      : null,
  )

  if (!resolution) return <InvestigationNotFound />

  return <InvestigationReadView id={resolution.id} slugs={resolution.slugs} />
}
